import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useEffect, useRef, useState } from 'react';
import Check from 'lucide-react/dist/esm/icons/check.mjs';
import LoaderCircle from 'lucide-react/dist/esm/icons/loader-circle.mjs';
import Pencil from 'lucide-react/dist/esm/icons/pencil.mjs';
import X from 'lucide-react/dist/esm/icons/x.mjs';
import { CHAT_TITLE_MAX_LENGTH, normalizeChatTitle } from './conversationTitles.js';
/** A rename request opens the editor only when it is a new, nonzero revision. */
export function isNewEditRequest(editRequest, lastSeen) {
    return editRequest !== 0 && editRequest !== lastSeen;
}
/**
 * `editRequest` (a revision) opens the editor once. The parent must clear its request in `onEditRequestHandled`: this
 * component unmounts while another view is shown, and a request still pending when it remounts would reopen the
 * editor with the title selected, so the user's next keystrokes would replace the chat name.
 */
export function ChatTitleEditor({ title, onRename, editRequest = 0, onEditRequestHandled }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(title);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const inputRef = useRef(null);
    const buttonRef = useRef(null);
    const savingRef = useRef(false);
    const editRequestRef = useRef(0);
    useEffect(() => {
        if (isNewEditRequest(editRequest, editRequestRef.current)) {
            setDraft(title);
            setEditing(true);
            setError('');
            onEditRequestHandled?.();
        }
        editRequestRef.current = editRequest;
    }, [editRequest, title, onEditRequestHandled]);
    useEffect(() => { if (editing) {
        inputRef.current?.focus();
        inputRef.current?.select();
    } }, [editing]);
    const close = () => { setEditing(false); setError(''); requestAnimationFrame(() => buttonRef.current?.focus()); };
    const save = async () => {
        if (savingRef.current)
            return;
        const next = normalizeChatTitle(draft);
        if (!next) {
            setError('Enter a chat name.');
            inputRef.current?.focus();
            return;
        }
        savingRef.current = true;
        setSaving(true);
        setError('');
        try {
            const result = await onRename(next);
            if (result === true)
                close();
            else
                setError(typeof result === 'object' ? result.error : 'Could not sync this name. Check your connection and try again.');
        }
        catch {
            setError('Could not rename this chat. Try again.');
        }
        finally {
            savingRef.current = false;
            setSaving(false);
        }
    };
    return _jsxs("div", { className: "gyro-chat-title", children: [editing ? _jsxs("form", { className: "gyro-chat-title-form", onSubmit: event => { event.preventDefault(); void save(); }, onKeyDown: event => {
                    if (event.key === 'Enter' && event.nativeEvent.isComposing) {
                        event.preventDefault();
                        return;
                    }
                    if (event.key === 'Escape') {
                        event.preventDefault();
                        event.stopPropagation();
                        if (!savingRef.current)
                            close();
                    }
                }, children: [_jsx("input", { ref: inputRef, "aria-label": "Chat name", value: draft, maxLength: CHAT_TITLE_MAX_LENGTH, disabled: saving, onChange: event => { setDraft(event.target.value); setError(''); } }), _jsx("button", { type: "submit", className: "gyro-btn ghost", "aria-label": "Save chat name", title: "Save name (Enter)", disabled: saving, children: saving ? _jsx(LoaderCircle, { size: 14, className: "gyro-file-spinner" }) : _jsx(Check, { size: 14 }) }), _jsx("button", { type: "button", className: "gyro-btn ghost", "aria-label": "Cancel rename", title: "Cancel (Escape)", disabled: saving, onClick: close, children: _jsx(X, { size: 14 }) })] }) : _jsx("h2", { className: "gyro-desktop-heading-title", children: _jsxs("button", { ref: buttonRef, type: "button", className: "gyro-chat-title-button", title: "Click to rename this chat", "aria-label": `Rename chat: ${title}`, onClick: () => { setDraft(title); setEditing(true); }, children: [_jsx("span", { children: title }), _jsx(Pencil, { size: 12, "aria-hidden": "true" })] }) }), error && _jsx("span", { className: "gyro-chat-title-error", role: "alert", children: error })] });
}
//# sourceMappingURL=ChatTitleEditor.js.map